import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, CalendarIcon, DollarSignIcon, StarIcon } from 'lucide-react';
export function Packages() {
  const [estimator, setEstimator] = useState({
    groupSize: 30,
    nights: 4,
    resortTier: 'premium',
    activities: 3,
    avLevel: 'standard',
    privateEvents: 2
  });
  const packages = [{
    title: 'Core Retreat',
    description: 'Perfect for team alignment and strategy sessions with focused outcomes.',
    idealFor: 'Leadership retreats, Team-building, department offsite, annual planning',
    nights: '3-4 nights',
    accommodationRange: '$350-500/night',
    inclusions: ['Accommodation in premium resorts', 'Airport transfers', 'Onsite Bilingual Concierge', 'Custom Check-in Experience', 'Travel Logistics support team', 'Venue Consulting'],
    brandExperience: ['Welcome Reception', 'Team Dinner Experience', 'Facilitated Strategy Session', 'Custom AV Production'],
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Incentive Sprints',
    description: 'Reward top performers with an unforgettable experience that drives future success.',
    idealFor: 'Sales Kick-offs, Performance-based rewards, milestone achievements, sales training',
    nights: '3-4 nights',
    accommodationRange: '$500-700/night',
    inclusions: ['Accommodation in premium resorts', 'Airport transfers', 'Onsite Bilingual Concierge', 'Custom Check-in Experience', 'Optional Companion program', 'Itinerary Building & Consulting', 'Travel Logistics support team', 'Venue Consulting'],
    brandExperience: ['Welcome gifts', 'Custom curated Awards Gala dinner', 'High-end entertainment & gourmet menu curation', 'Personalized concierge service', 'Team-building excursions', 'Professional Photography'],
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Ultra Brand Launch',
    description: 'Create buzz and alignment around your new product or initiative.',
    idealFor: 'Product launches, rebrands, major announcements, supplier & vendor appreciation, networking',
    nights: '3-5 nights',
    accommodationRange: '$350-700/night',
    inclusions: ['Accommodation in premium resorts', 'Airport transfers', 'Onsite Bilingual Concierge', 'Custom Check-in Experience', 'Optional Companion program', 'Standard Graphic Design services', 'Itinerary Building & Consulting', 'Travel Logistics support team', 'Venue Consulting'],
    brandExperience: ['Custom Event App', 'Branded welcome experience', 'Custom AV Production w/ Expert Consultant', 'Brand seminars', 'Training workshops', 'Product showcase', 'PR & Communication campaigns'],
    image: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Executive Retreats',
    description: 'Elite experiences designed for C-Suite executives and board members.',
    idealFor: 'Learning & Development, C-Suite & Executive Retreats, Board Meetings',
    nights: '3-5 nights',
    accommodationRange: '$800-$1500/night',
    inclusions: ['Accommodation in premium resorts', 'Private Black Car Airport transfer service', 'Onsite Bilingual White-Glove Concierge', 'Custom Check-in Experience', 'Itinerary Building & Consulting from our Global Expert Network', 'Travel Logistics support team', 'Venue Consulting'],
    brandExperience: ['Branded welcome experience', 'Custom AV Production w/ Expert Consultant', 'Invite speakers & trainers from our Global Expert Network'],
    image: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'All-Hands',
    description: 'Company-wide gatherings that bring everyone together for celebration and alignment.',
    idealFor: 'Annual General meetings, company affiliates/vendors appreciation, holiday celebrations',
    nights: '3-4 nights',
    accommodationRange: '$350-500/night',
    inclusions: ['Accommodation in premium resorts', 'Airport transfers', 'Onsite Bilingual Concierge', 'Custom Check-in Experience', 'Travel Logistics support team', 'Venue Consulting'],
    brandExperience: ['Custom Event App', 'Branded welcome experience', 'Custom AV Production w/ Expert Consultant', 'Brand seminars', 'Training workshops', 'Product showcase', 'Curated dinner galas & events', 'Curated team builders'],
    image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
  }];
  const handleEstimatorChange = (field, value) => {
    setEstimator({
      ...estimator,
      [field]: value
    });
  };
  // Updated estimation calculation
  const calculateEstimate = () => {
    const baseCost = {
      standard: 425,
      premium: 600,
      luxury: 850,
      executive: 1100
    }[estimator.resortTier];
    const activityCost = estimator.activities * 150;
    const avCost = {
      basic: 1500,
      standard: 4000,
      advanced: 15000
    }[estimator.avLevel];
    const eventCost = estimator.privateEvents * avCost;
    const perPersonPerNight = baseCost + (activityCost + eventCost) / (estimator.groupSize * estimator.nights);
    const totalLow = Math.round(perPersonPerNight * 0.9 * estimator.groupSize * estimator.nights);
    const totalHigh = Math.round(perPersonPerNight * 1.1 * estimator.groupSize * estimator.nights);
    return {
      perPerson: `$${Math.round(perPersonPerNight * 0.9)}-$${Math.round(perPersonPerNight * 1.1)}`,
      total: `$${totalLow.toLocaleString()}-$${totalHigh.toLocaleString()}`
    };
  };
  const estimate = calculateEstimate();
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-2689">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-2690">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2691">
          <div className="max-w-3xl" data-id="element-2692">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-2693">
              Solutions
            </h1>
            <p className="text-xl text-white/90" data-id="element-2694">
              Tailored solutions for your organization's unique goals and
              budget.
            </p>
          </div>
        </div>
      </section>

      {/* Signature Experiences */}
      <section className="py-16 bg-white" data-id="element-2695">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2696">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2697">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2698">
              EXPERIENCES
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-2699">
              Choose from our most popular packages, each customizable to your
              specific needs.
            </p>
          </div>
          <div className="space-y-12" data-id="element-2700">
            {packages.map((pkg, index) => <Card key={index} className="overflow-hidden" data-id="element-2701">
                <div className="grid md:grid-cols-3 gap-6" data-id="element-2702">
                  <div className="md:col-span-1" data-id="element-2703">
                    <div className="h-full" data-id="element-2704">
                      <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover" data-id="element-2705" />
                    </div>
                  </div>
                  <div className="md:col-span-2 p-6" data-id="element-2706">
                    <h3 className="text-2xl font-bold text-[#374047] mb-2" data-id="element-2707">
                      {pkg.title}
                    </h3>
                    <p className="text-[#374047]/70 mb-4" data-id="element-2708">{pkg.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-2709">
                      <div data-id="element-2710">
                        <p className="text-sm text-[#374047]/60" data-id="element-2711">Ideal For:</p>
                        <p className="font-medium text-sm" data-id="element-2712">{pkg.idealFor}</p>
                      </div>
                      <div data-id="element-2713">
                        <p className="text-sm text-[#374047]/60" data-id="element-2714">Duration:</p>
                        <p className="font-medium" data-id="element-2715">{pkg.nights}</p>
                      </div>
                      <div data-id="element-2716">
                        <p className="text-sm text-[#374047]/60" data-id="element-2717">
                          Accommodation Range:
                        </p>
                        <p className="font-medium" data-id="element-2718">{pkg.accommodationRange}</p>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-[#0099A1] mb-3" data-id="element-2719">
                      Inclusions:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4" data-id="element-2720">
                      {pkg.inclusions.map((inclusion, i) => <div key={i} className="flex items-start" data-id="element-2721">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2722" />
                          <span className="ml-2 text-sm" data-id="element-2723">{inclusion}</span>
                        </div>)}
                    </div>
                    <h4 className="text-lg font-semibold text-[#0099A1] mb-3" data-id="element-2724">
                      Create your brand experience w/:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6" data-id="element-2725">
                      {pkg.brandExperience.map((item, i) => <div key={i} className="flex items-start" data-id="element-2726">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2727" />
                          <span className="ml-2 text-sm" data-id="element-2728">{item}</span>
                        </div>)}
                    </div>
                    <div className="flex flex-wrap gap-3" data-id="element-2729">
                      <Button variant="primary" to="/contact" data-id="element-2730">
                        Request Proposal
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Quick Estimator */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-2731">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2732">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2733">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2734">
              Quick Estimator
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-2735">
              Get a ballpark estimate for your event. Adjust the parameters
              below to see how they affect pricing.
            </p>
          </div>
          <Card className="max-w-3xl mx-auto" data-id="element-2736">
            <CardBody data-id="element-2737">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-2738">
                <div data-id="element-2739">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-2740">
                    Group Size
                  </label>
                  <input type="range" min="10" max="200" value={estimator.groupSize} onChange={e => handleEstimatorChange('groupSize', parseInt(e.target.value))} className="w-full" data-id="element-2741" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-2742">
                    <span data-id="element-2743">10</span>
                    <span data-id="element-2744">{estimator.groupSize} people</span>
                    <span data-id="element-2745">200</span>
                  </div>
                </div>
                <div data-id="element-2746">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-2747">
                    Number of Nights
                  </label>
                  <input type="range" min="2" max="7" value={estimator.nights} onChange={e => handleEstimatorChange('nights', parseInt(e.target.value))} className="w-full" data-id="element-2748" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-2749">
                    <span data-id="element-2750">2</span>
                    <span data-id="element-2751">{estimator.nights} nights</span>
                    <span data-id="element-2752">7</span>
                  </div>
                </div>
                <div data-id="element-2753">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-2754">
                    Resort Tier
                  </label>
                  <select value={estimator.resortTier} onChange={e => handleEstimatorChange('resortTier', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2755">
                    <option value="standard" data-id="element-2756">
                      Standard $350-$500/night/pp
                    </option>
                    <option value="premium" data-id="element-2757">Premium $500-$700/night/pp</option>
                    <option value="luxury" data-id="element-2758">Luxury $700-$1000/night/pp</option>
                    <option value="executive" data-id="element-2759">
                      Executive $1000-$1200/night/pp
                    </option>
                  </select>
                </div>
                <div data-id="element-2760">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-2761">
                    Number of Activities
                  </label>
                  <input type="range" min="1" max="10" value={estimator.activities} onChange={e => handleEstimatorChange('activities', parseInt(e.target.value))} className="w-full" data-id="element-2762" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-2763">
                    <span data-id="element-2764">1</span>
                    <span data-id="element-2765">{estimator.activities} activities</span>
                    <span data-id="element-2766">10</span>
                  </div>
                </div>
                <div data-id="element-2767">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-2768">
                    AV Production
                  </label>
                  <select value={estimator.avLevel} onChange={e => handleEstimatorChange('avLevel', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2769">
                    <option value="basic" data-id="element-2770">Basic $1500/private event</option>
                    <option value="standard" data-id="element-2771">
                      Standard $4000/private event
                    </option>
                    <option value="advanced" data-id="element-2772">
                      Advanced $15000/private event
                    </option>
                  </select>
                </div>
                <div data-id="element-2773">
                  <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-2774">
                    Private Events
                  </label>
                  <input type="range" min="0" max="5" value={estimator.privateEvents} onChange={e => handleEstimatorChange('privateEvents', parseInt(e.target.value))} className="w-full" data-id="element-2775" />
                  <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-2776">
                    <span data-id="element-2777">0</span>
                    <span data-id="element-2778">{estimator.privateEvents} events</span>
                    <span data-id="element-2779">5</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#0099A1]/10 rounded-lg p-6" data-id="element-2780">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2781">
                  <div className="text-center" data-id="element-2782">
                    <p className="text-sm text-[#374047]/70 mb-1" data-id="element-2783">
                      Estimated Per Person Per Night
                    </p>
                    <p className="text-2xl font-bold text-[#0099A1]" data-id="element-2784">
                      {estimate.perPerson}
                    </p>
                  </div>
                  <div className="text-center" data-id="element-2785">
                    <p className="text-sm text-[#374047]/70 mb-1" data-id="element-2786">
                      Estimated Total Budget
                    </p>
                    <p className="text-2xl font-bold text-[#0099A1]" data-id="element-2787">
                      {estimate.total}
                    </p>
                  </div>
                </div>
                <p className="text-center text-xs text-[#374047]/70 mt-4" data-id="element-2788">
                  *Estimates are subject to proposal and may vary based on
                  specific requirements, seasonality, and availability.
                </p>
              </div>
            </CardBody>
            <CardFooter className="justify-center" data-id="element-2789">
              <Button variant="primary" to="/contact" data-id="element-2790">
                Request Detailed Proposal
              </Button>
            </CardFooter>
          </Card>
        </div>
      </section>

      <CtaSection data-id="element-2791" />
    </div>;
}